import { UPLOAD_MAX_BYTES } from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { meetingsApi } from '../lib/api.js';

export function UploadPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { data } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  const accept = (picked: File | undefined): void => {
    if (!picked) return;
    if (picked.size > UPLOAD_MAX_BYTES) {
      setError(t('upload.tooBig'));
      return;
    }
    setError(null);
    setFile(picked);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    accept(e.dataTransfer.files[0]);
  };

  const submit = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await meetingsApi.upload(id, file, setProgress);
      setDone(true);
      navigate(`/meetings/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1>{data?.meeting.title ?? t('upload.title')}</h1>
      {error && <div className="banner error">{error}</div>}
      {done && <div className="banner">{t('upload.done')}</div>}

      <div
        className="card"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.click();
        }}
        style={{ textAlign: 'center', cursor: 'pointer', padding: 32 }}
      >
        <p style={{ margin: 0 }}>{file ? file.name : t('upload.drop')}</p>
        {file && (
          <p className="muted" style={{ marginBottom: 0 }}>
            {(file.size / (1024 * 1024)).toFixed(1)} MB
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,video/*"
          hidden
          onChange={(e) => accept(e.target.files?.[0])}
        />
      </div>

      {busy && (
        <div className="card">
          <p className="muted">
            {t('upload.uploading')} — {progress}%
          </p>
          <div style={{ background: 'var(--ivory-2)', borderRadius: 999, height: 8 }}>
            <div
              style={{
                width: `${progress}%`,
                background: 'var(--gold)',
                height: 8,
                borderRadius: 999,
                transition: 'width 120ms linear',
              }}
            />
          </div>
        </div>
      )}

      <button type="button" className="gold" disabled={!file || busy} onClick={() => void submit()}>
        {t('upload.submit')}
      </button>
    </>
  );
}
